// react
import { useContext, useMemo, useState } from 'react';

// types
import { Activity, Pagination } from '../../../../@types';

// unions
import { activityTypes } from '../../../../@types/unions';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject } from '../../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// mui
import { Grid } from '@mui/material';

// cards
import { CardActivity } from '../../../cards/CardActivity/CardActivity';
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';

// ui
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { FilterChangeEvent } from '../../../ui/Filters/Filters';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// icons
import { TimerOutlined as IconTimer } from '@mui/icons-material';

// utils
import { translateActivityType } from '../../../../utils/translations';

// props
export interface TableActivitiesProps {
	activities: Activity[];
	hasSearch?: boolean;
	isLoading?: boolean;
	pagination?: Pagination;
	refetchActivities?: () => void;
}

export const TableActivities = ({
	activities,
	hasSearch = true,
	isLoading,
	pagination,
	refetchActivities,
}: TableActivitiesProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [users, setUsers] = useState<{ id: number; name: string }[]>([]);

	// params
	const paramActor = searchParams.get('actor');
	const paramType = searchParams.get('type');

	// defaults
	const defaultFiltersValues = {
		actor: 'all',
		type: 'all',
	};

	// state - filters
	const [filtersValues, setFiltersValues] = useState({
		actor: paramActor || defaultFiltersValues.actor,
		type: paramType || defaultFiltersValues.type,
	});

	// fetch users with activities (for actor filter)
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/users?projects=${project?.id}&hasActivities=true&fields=id,firstName,lastName`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				const actors = res.data.map(
					(user: { id: number; firstName: string; lastName: string }) => ({
						id: user.id,
						name: `${user.firstName} ${user.lastName}`,
					})
				);
				setUsers(actors);
			}
		},
	});

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		const newFiltersValues = {
			...filtersValues,
			[name]: value,
		};
		setFiltersValues(newFiltersValues);

		// Update search params
		if (value && value !== 'all') {
			searchParams.set(name, value);
		} else {
			searchParams.delete(name);
		}
		searchParams.set('page', '1'); // Reset to first page when filtering
		setSearchParams(searchParams);
	};

	const handleResetFilters = () => {
		setFiltersValues(defaultFiltersValues);
		searchParams.delete('actor');
		searchParams.delete('type');
		searchParams.set('page', '1');
		setSearchParams(searchParams);
	};

	// headers
	const headers: TableHeaderProps[] = [
		{
			GridProps: { lg: 6 },
			isSortable: true,
			label: t('activity'),
			value: 'type',
		},
		{
			GridProps: { lg: 1.5 },
			isSortable: false,
			label: t('source'),
			value: 'source',
		},
		{
			GridProps: { lg: 1.5 },
			isSortable: false,
			label: t('status'),
			value: 'status',
		},
		{
			GridProps: { lg: 2 },
			isSortable: true,
			label: t('created'),
			value: 'created',
		},
		{
			GridProps: { lg: 0.5 },
			isSortable: false,
			label: '',
			value: 'actions',
		},
	];

	// activity type options for filtering
	const optionsActivityTypes = useMemo(() => {
		const options = [{ label: t('all'), value: 'all' }];

		// Add options from activityTypes union
		const typeOptions = activityTypes.map((type) => ({
			label: translateActivityType(type, t),
			value: type,
		}));
		options.push(...typeOptions);

		return options;
	}, [t]);

	// actor options for filtering
	const optionsUsers = useMemo(() => {
		const options = [{ label: t('all'), value: 'all' }];

		// Add options from users
		if (users.length) {
			const userOptions = users.map((user) => ({
				label: user.name,
				value: String(user.id),
			}));
			options.push(...userOptions);
		}

		return options;
	}, [users, t]);

	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: {
				children: t('empty.activity.content'),
			},
			IconCircleProps: {
				icon: <IconTimer />,
			},
			title: { children: t('empty.activity.title') },
		};

		return emptyProps;
	}, [t]);

	const content = useMemo(() => {
		if (isLoading || !activities.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{activities.map((activity, i) => {
					return (
						<Grid item key={`activity-${i}-${activity.created}`} xs={12}>
							<CardActivity activity={activity} refetchActivities={refetchActivities} />
						</Grid>
					);
				})}
			</Grid>
		);
	}, [activities, empty, isLoading, refetchActivities]);

	return (
		<Grid container spacing={2}>
			{hasSearch && (
				<Grid item xs={12}>
					<Grid container spacing={2}>
						<Grid item xs={12} md={true}>
							<FormSearch InputProps={{ placeholder: t('searchByName') }} />
						</Grid>
						<Grid item xs={12} md="auto">
							<BtnFilters
								filters={[
									{
										hasAllOption: false,
										hasNoneOption: false,
										hasSearchParam: true,
										label: t('activityType'),
										name: 'type',
										onChange: handleChangeFilterValue,
										options: optionsActivityTypes,
										required: true,
										type: 'SELECT',
										value: filtersValues.type,
									},
									{
										hasAllOption: false,
										hasNoneOption: false,
										hasSearchParam: true,
										label: t('user'),
										name: 'actor',
										onChange: handleChangeFilterValue,
										options: optionsUsers,
										required: true,
										type: 'SELECT',
										value: filtersValues.actor,
									},
								]}
								onReset={handleResetFilters}
							/>
						</Grid>
					</Grid>
				</Grid>
			)}
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			{pagination && (
				<Grid item xs={12}>
					<TableFooter numShowing={activities.length} pagination={pagination} />
				</Grid>
			)}
		</Grid>
	);
};
